import type {
  AcceptedTerms,
  ResolvedTerms,
  TermsScope,
} from '@/services/terms';
import type { Tenant } from '@/state/tenant';
import { TERMS_ACCEPTANCE_ERROR_CODES } from '@/lib/constants';
import { getErrorCode } from '@/lib/error-utils';
import { joinWithAnd } from '@/helpers/public-terms';
import type { ClassCartItem } from '@/components/features/checkout/class-checkout';
import type { ClassPackCartItem } from '@/components/features/checkout/class-pack-checkout';
import type { SubscriptionCartItem } from '@/components/features/checkout/subscription-checkout';
import type { CheckoutType, GiftCardCartItem } from '@/lib/types';

export type TermsTag =
  | 'studio_name'
  | 'studio_address'
  | 'support_email'
  | 'billing_interval'
  | 'pack_expiry_days'
  | 'cancellation_notice_hours'
  | 'country'
  | 'company_number'
  | 'vat_id'
  | 'dispute_body';

export type TermsTagValues = Partial<Record<TermsTag, string>>;

const TAG_FALLBACKS: Record<TermsTag, string> = {
  studio_name: 'the studio',
  studio_address: 'listed on our website',
  support_email: 'our support email',
  billing_interval: 'at regular intervals',
  // Admin no longer generates this tag, but documents published while it
  // existed still contain {{pack_expiry_days}} and must keep rendering.
  pack_expiry_days: 'a set number of',
  cancellation_notice_hours: 'a few',
  country: 'your country',
  // Always baked in at publish time; fallbacks exist to satisfy the
  // known-tag lookup.
  company_number: '',
  vat_id: '',
  dispute_body: 'a consumer arbitration body',
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Object.hasOwn, not `in`: a typed token like {{constructor}} must not match
// inherited Object.prototype keys.
const isKnownTag = (tag: string): tag is TermsTag =>
  Object.hasOwn(TAG_FALLBACKS, tag);

export const replaceTermsTags = (html: string, values: TermsTagValues = {}) =>
  html.replace(/\{\{(\w+)\}\}/g, (match, tag: string) => {
    if (!isKnownTag(tag)) return '';
    const value = values[tag];
    return value ? escapeHtml(value) : TAG_FALLBACKS[tag];
  });

export const getTermsAcceptanceKey = (docs: ResolvedTerms[]) =>
  docs.map((doc) => `${doc.terms_document_id}:${doc.version}`).join('|');

export const toAcceptedTerms = (docs: ResolvedTerms[]): AcceptedTerms[] =>
  docs.map((doc) => ({
    terms_document_id: doc.terms_document_id,
    version: doc.version,
  }));

export const isTermsAcceptanceError = (error: unknown): error is Error =>
  TERMS_ACCEPTANCE_ERROR_CODES.includes(getErrorCode(error) ?? '');

export const TERMS_REQUIRED_MESSAGE =
  'Please accept the terms to continue with your purchase.';

export const TERMS_CHANGED_MESSAGE =
  'The terms have changed. Review and accept them to continue.';

// The server's refusal is worded for a purchase ("this purchase", "opened
// checkout"), and the waitlist join asks for the same acceptance, so it is
// never shown: a surface where nothing is bought passes its own sentence.
export const termsRefusalMessage = (
  changedMessage: string | undefined
): string => changedMessage || TERMS_CHANGED_MESSAGE;

// The box a host ticks for their guests, followed by the terms' titles as
// links. The admin hand-copies it as GUEST_TERMS_LINE
// (src/helpers/terms-acceptances.ts) and prints it on each guest's acceptance
// row, always in the singular since a row is one guest's: change both
// together.
export const GUEST_TERMS_LINE = {
  lead: (studioName: string, guests = 1) =>
    `${guests > 1 ? 'My guests agree' : 'My guest agrees'} to ${studioName}'s`,
};

// Each without its closing full stop: they are clauses of the one checkbox
// sentence.
export const getTermsAcknowledgments = (docs: ResolvedTerms[]): string[] => [
  ...new Set(
    docs
      .map((doc) => doc.checkout_acknowledgment?.trim().replace(/\.$/, ''))
      .filter((text): text is string => !!text)
  ),
];

// Completes "I agree to the {documents}".
export const acknowledgmentClause = (acknowledgments: string[]): string => {
  if (!acknowledgments.length) return '';
  return acknowledgments.length === 1
    ? ` and ${acknowledgments[0]}`
    : `, ${joinWithAnd(acknowledgments)}`;
};

// Mirrors GIFT_CARD_CHECKOUT_ACKNOWLEDGMENT in mokoboko-admin's
// src/helpers/terms-checkout.ts, the sentence a Gift cards document stores.
export const GIFT_CARD_CHECKOUT_ACKNOWLEDGMENT =
  'want the gift card to work right away.';

// Class terms carry no request of their own, and the studio-wide fallback's
// credits wording does not apply to a single class purchase. A document only
// stores a request where the law gives a 14-day right, and a studio-wide one
// stores the credits sentence, so on a gift card any stored request becomes
// the gift card's own. mokoboko-admin's getShownAcknowledgment repeats this
// rule to print the request on the acceptance evidence.
export const getCheckoutAcknowledgments = (
  type: TermsScope,
  docs: ResolvedTerms[]
): string[] => {
  if (type === 'CLASS') return [];
  const acknowledgments = getTermsAcknowledgments(docs);
  return type === 'GIFT_CARD' && acknowledgments.length
    ? [GIFT_CARD_CHECKOUT_ACKNOWLEDGMENT.replace(/\.$/, '')]
    : acknowledgments;
};

export const getTermsTagValues = (
  tenant?: Tenant,
  billingInterval?: string | null
): TermsTagValues => ({
  ...(tenant?.name && { studio_name: tenant.name }),
  ...(tenant?.address && { studio_address: tenant.address }),
  ...(tenant?.support_email && { support_email: tenant.support_email }),
  ...(billingInterval && { billing_interval: billingInterval }),
});

export const CHECKOUT_TERMS_SCOPE: Record<CheckoutType, TermsScope> = {
  class: 'CLASS',
  'class-pack': 'CLASS_PACK',
  subscription: 'SUBSCRIPTION',
  'gift-card': 'GIFT_CARD',
};

export const getCartTermsEntityIds = (
  checkoutType: CheckoutType,
  cartItems: (
    | ClassCartItem
    | ClassPackCartItem
    | SubscriptionCartItem
    | GiftCardCartItem
  )[]
): string[] => {
  const ids = cartItems.map((item) => {
    if (checkoutType === 'class-pack')
      return (item as ClassPackCartItem).class_pack_id;
    if (checkoutType === 'subscription')
      return (item as SubscriptionCartItem).subscription_id;
    if (checkoutType === 'gift-card')
      return (item as GiftCardCartItem).gift_card_id;
    return (item as ClassCartItem).class_id;
  });
  return [...new Set(ids)];
};
